import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import WorkSansRegular from 'assets/fonts/WorkSans-Regular.ttf';
import WorkSansBold from 'assets/fonts/WorkSans-Bold.ttf';
import WorkSansMedium from 'assets/fonts/WorkSans-Medium.ttf';
import { IPrintCitationListProps } from './print-citation-list.types';
import LogoIcon from 'assets/icons/eiq-logo-full-r.png';
import { CitationStatus, citationStatusMap } from 'features/eiq-manage/enums/citation-status.enum';
import dayjs from 'dayjs';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import { TrafficLogVisitorType } from 'common/constans/common.constants';
import { visitorTypeMap } from 'common/constans/visitor.constants';
import { StrToDateTimeLocal } from 'common/helpers/date.helper';

const PrintCitationList = ({ citations, dateFrom, dateTo }: IPrintCitationListProps) => {
  Font.registerHyphenationCallback((w) => [w]);
  Font.register({
    family: 'Work Sans',
    fonts: [
      { src: WorkSansRegular, fontWeight: 400 },
      { src: WorkSansBold, fontWeight: 600 },
      { src: WorkSansMedium, fontWeight: 500 },
    ],
  });

  const getRegisteredTo = ({ residentName, guestName, companyName }: ICitationRead) => {
    if (!residentName) {
      return '';
    }

    let result = residentName;

    if (guestName) {
      result += ` - ${guestName}`;
    }
    if (companyName) {
      result += ` (${companyName})`;
    }

    return result;
  };

  const getCitationConsequences = ({ totalPaymentAmount, suspensionDays, status }: ICitationRead) => {
    if (status === CitationStatus.Warning) {
      return 'Warning only';
    }

    if (totalPaymentAmount && suspensionDays) {
      return `$${totalPaymentAmount} fine with ${suspensionDays} days suspension`;
    }

    if (totalPaymentAmount) {
      return `$${totalPaymentAmount} fine`;
    }

    if (suspensionDays) {
      return `${suspensionDays} days suspension`;
    }

    return '';
  };

  const Header = () => {
    return (
      <View style={styles.header}>
        <Image src={LogoIcon} style={styles.logo} />
      </View>
    );
  };

  const ListItemRow = ({ title, value }: { title: string; value: string | number }) => {
    return (
      <Text>
        <Text style={[styles.bold, styles.text]}>{title} </Text>
        <Text style={[styles.text]}>{value}</Text>
      </Text>
    );
  };

  const ListItem = ({ citation, index }: { citation: ICitationRead; index: number }) => {
    return (
      <View style={styles.listItem} key={`citation-list-item-${index}`} wrap={false}>
        <View style={styles.listItemColumn}>
          <ListItemRow title={'Citation#:'} value={citation.externalId} />
          <ListItemRow title={'Date & Time:'} value={StrToDateTimeLocal(citation.occurredAt)?.format('MM/DD/YYYY h:mmA') ?? ''} />
          <ListItemRow title={'License Plate:'} value={citation.licensePlate} />
          <ListItemRow title={'Registered to:'} value={getRegisteredTo(citation)} />
          <ListItemRow title={'Type:'} value={visitorTypeMap[citation.ownerType as TrafficLogVisitorType]} />
        </View>
        <View style={styles.listItemColumn}>
          <ListItemRow title={'Speed Limit:'} value={`${citation.speedLimit} mph`} />
          <ListItemRow title={'Recorded Speed:'} value={`${citation.violatingSpeed} mph`} />
          <ListItemRow title={'Consequences:'} value={getCitationConsequences(citation)} />
          <ListItemRow title={'Status:'} value={citationStatusMap[citation.status as CitationStatus]} />
        </View>
      </View>
    );
  };

  const Footer = () => {
    return (
      <View style={styles.footer} fixed>
        <Text>EIQ-Intelliticket - Violations Report</Text>
        <Text render={({ pageNumber }) => `Page ${pageNumber}`} />
      </View>
    );
  };

  return (
    <Document>
      <Page size={'A4'} style={styles.page}>
        <Header />
        <View>
          <Text style={[styles.title, styles.bold]}>
            Speed Violation Report: {dateFrom} - {dateTo}
          </Text>
        </View>
        <View style={styles.summary}>
          <Text style={[styles.bold, styles.text]}>Report Summary </Text>
          <Text style={[styles.text]}>{`Total Citations: ${citations.length}`}</Text>
          <Text style={[styles.text]}>
            {`Valid Citations (Excluding Pending/Canceled): ${
              citations.filter((i) => i.status !== CitationStatus.Pending && i.status !== CitationStatus.Canceled).length
            }`}
          </Text>
          <Text style={[styles.text]}>{`Confirmed: ${
            citations.filter((i) => i.status === CitationStatus.PayNow || i.status === CitationStatus.Delinquent).length
          }`}</Text>
          <Text style={[styles.text]}>{`Paid: ${citations.filter((i) => i.status === CitationStatus.Paid).length}`}</Text>
          <Text style={[styles.text]}>{`Warning: ${citations.filter((i) => i.status === CitationStatus.Warning).length}`}</Text>
          <Text style={[styles.text]}>{`Generated on: ${dayjs().format('MM/DD/YYYY hh:mm A')}`}</Text>
        </View>
        <View style={styles.listing}>
          <Text style={[styles.title, styles.bold]}>Citations List</Text>
        </View>
        {citations.map((item, index) => {
          return <ListItem citation={item} index={index} />;
        })}
        <Footer />
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'absolute',
    bottom: 10,
    left: 20,
    right: 20,
    height: 20,
    fontSize: 10,
  },
  listItemColumn: { width: '50%' },
  listItem: { display: 'flex', flexDirection: 'row', marginBottom: 10, marginTop: 10, borderBottom: '1pt solid #292B2E', padding: 4 },
  listing: { marginTop: 20, marginBottom: 10 },
  summary: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 20 },
  text: { color: '#292B2E', fontSize: 12 },
  title: { color: '#292B2E', fontSize: 14 },
  bold: { fontWeight: 500 },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'center',
    marginBottom: 30,
  },
  logo: {
    width: 120,
    marginTop: 20,
  },
  page: {
    paddingTop: 15,
    paddingHorizontal: 20,
    paddingBottom: 60,
    fontFamily: 'Work Sans',
    color: '#292B2E',
  },
});

export default PrintCitationList;
